import { sql } from 'drizzle-orm'
import { db } from '@/db'
import {
  KNOWLEDGE_EMBEDDING_DIMENSIONS,
  KNOWLEDGE_EMBEDDING_MODEL,
  knowledgeEntries,
  type KnowledgeEntryKind,
} from '@/db/schema'

const SLUG_MAX_LENGTH = 96

export type KnowledgeEntryInput = {
  title: string
  bodyMarkdown: string
  kind?: KnowledgeEntryKind
  slug?: string
}

export function normalizeKnowledgeInput(input: KnowledgeEntryInput): {
  title: string
  slug: string
  bodyMarkdown: string
  flatMarkdown: string
  kind: KnowledgeEntryKind
} {
  const title = input.title.trim()
  const bodyMarkdown = input.bodyMarkdown.trim()
  const kind = input.kind ?? 'concept'
  const slug = (input.slug?.trim() || slugify(title)).slice(0, SLUG_MAX_LENGTH)

  if (title.length < 3) throw new Error('Title must be at least 3 characters')
  if (!slug) throw new Error('Slug could not be generated')
  if (bodyMarkdown.length < 10) throw new Error('Markdown body must be at least 10 characters')

  return {
    title,
    slug,
    kind,
    bodyMarkdown,
    flatMarkdown: renderFlatMarkdown({ title, kind, bodyMarkdown }),
  }
}

export function renderFlatMarkdown(input: {
  title: string
  kind: KnowledgeEntryKind
  bodyMarkdown: string
}): string {
  return [`# ${input.title}`, '', `kind: ${input.kind}`, '', input.bodyMarkdown.trim(), ''].join('\n')
}

export function slugify(value: string): string {
  return value
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function buildKnowledgeEmbedding(text: string): number[] {
  const vector = Array.from({ length: KNOWLEDGE_EMBEDDING_DIMENSIONS }, () => 0)
  const tokens = text.toLowerCase().match(/[a-z0-9]+/g) ?? []

  for (const token of tokens) {
    const hash = hashToken(token)
    const index = hash % KNOWLEDGE_EMBEDDING_DIMENSIONS
    vector[index] += 1

    if (token.length > 4) {
      const stemHash = hashToken(token.slice(0, 4))
      vector[stemHash % KNOWLEDGE_EMBEDDING_DIMENSIONS] += 0.5
    }
  }

  const magnitude = Math.sqrt(vector.reduce((sum, value) => sum + value * value, 0))
  if (magnitude === 0) return vector
  return vector.map((value) => Number((value / magnitude).toFixed(6)))
}

export async function upsertKnowledgeEmbedding(entryId: string): Promise<void> {
  const [entry] = await db
    .select({
      id: knowledgeEntries.id,
      title: knowledgeEntries.title,
      flatMarkdown: knowledgeEntries.flatMarkdown,
    })
    .from(knowledgeEntries)
    .where(sql`${knowledgeEntries.id} = ${entryId}`)
    .limit(1)

  if (!entry) throw new Error(`Knowledge entry not found: ${entryId}`)

  const embedding = buildKnowledgeEmbedding(`${entry.title}\n${entry.flatMarkdown}`)
  await db
    .update(knowledgeEntries)
    .set({
      embedding,
      embeddingModel: KNOWLEDGE_EMBEDDING_MODEL,
      embeddingUpdatedAt: new Date(),
      updatedAt: new Date(),
    })
    .where(sql`${knowledgeEntries.id} = ${entryId}`)
}

function hashToken(token: string): number {
  let hash = 2166136261
  for (let i = 0; i < token.length; i += 1) {
    hash ^= token.charCodeAt(i)
    hash = Math.imul(hash, 16777619)
  }
  return hash >>> 0
}
